import {
  Component,
  computed,
  ElementRef,
  Input,
  OnChanges,
  OnInit,
  signal,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { toCidr } from '@shared/utils/data-utils';
import { IpWithComment } from '@shared/services/organization-site.service';
import { BehaviorSubject, catchError, Observable, throwError } from 'rxjs';
import { isValidIpOrCidr } from '@shared/validators/validators';
import { ToastrService } from '@shared/services/toastr.service';
import { arrayPushItem, arrayRemoveItem, arrayReplaceItem, hasDuplicates } from '@shared/utils/lang';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { FeatureFlagService } from '@shared/services/feature-flag.service';
import { parseBulkLines } from '@shared/utils/clipboard';
import { ConfirmService } from '@shared/services/confirm.service';
import { NgClass, NgIf, NgStyle } from '@angular/common';
import { HelpComponent } from '@shared/components/help/help.component';
import { FormsModule } from '@angular/forms';
import { DisableOnClickDirective } from '@shared/directives/disable-button/disable-on-click.directive';
import { map } from 'rxjs/operators';

@Component({
  selector: 'app-ip-list',
  templateUrl: './ip-list.component.html',
  styleUrls: ['./ip-list.component.scss', '../../add-site.component.scss'],
  imports: [NgIf, HelpComponent, FormsModule, DisableOnClickDirective, NgStyle, NgClass, TranslatePipe],
})
export class IpListComponent implements OnInit, OnChanges {
  @Input({ required: true }) domainName: string;
  @Input({ required: false }) ipListService: IpListService;
  @Input({ required: true }) titleKey: string;
  @Input() descriptionKey: string;
  @Input() addLabelKey: string;
  @Input({ required: true }) noneLabel: string;

  ips = signal<IpVM[]>([]);

  busy = new BehaviorSubject<boolean>(false);
  tmpIpException: IpVM;

  protected pasting = false;

  @ViewChild('ipInput') ipExceptionElement: ElementRef;

  isEditing = computed(() => this.ips().some((ipException) => ipException.editing));

  selectedExceptions = computed(() => this.ips().filter((ipException) => ipException.selected));

  allSelected = computed(() => {
    const items = this.ips();
    return items.length > 0 && items.every((ipException) => ipException.selected);
  });

  constructor(
    private toastr: ToastrService,
    private translate: TranslateService,
    protected featureFlagService: FeatureFlagService,
    private confirmService: ConfirmService,
  ) {}

  ngOnInit(): void {
    this.loadIpExceptions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['domainName'] && !changes['domainName'].firstChange) {
      this.loadIpExceptions();
    }
  }

  loadIpExceptions() {
    this.ips.set([]);
    const currentService = this.ipListService;
    currentService.getIps().subscribe({
      next: (res) => {
        this.ips.set(res.map((item, index) => ({ ...item, editing: false, pos: index })));
      },
      error: (error: any) => {
        this.toastr.error('Failed to load whitelisted IPs');
        console.error('Error : ', error);
      },
    });
  }

  addIpException() {
    if (this.isEditing()) return;
    this.ips.update(arrayPushItem({ ip: '', editing: true, isNew: true }));
    setTimeout(() => this.ipExceptionElement.nativeElement.focus(), 1);
  }

  createIpException(ipException: IpVM) {
    const error = this.validationError(ipException);
    if (error) {
      this.toastr.error(error);
      this.busy.next(false);
      return;
    }

    this.busy.next(true);
    this.saveIps().subscribe((res) => {
      this.addIpException();
      this.toastr.success('WhitelistedIPAdded'); // FIXME: configure success message
    });
  }

  saveIps() {
    return this.ipListService
      .setIps(
        this.ips().map((ip) => ({ ip: toCidr(ip.ip), comment: ip.comment })),
      )
      .pipe(
        map((res) => {
          this.busy.next(false);
          this.ips.set(
            res.map((ipWithComment, index) => {
              return { ...this.ipExceptionToVm(ipWithComment), pos: index };
            }),
          );
        }),
        catchError((err) => {
          this.busy.next(false);
          this.toastr.error('EnregistrementEchoue');
          this.pasting = false;
          return throwError(err);
        }),
      );
  }

  toggleSelected(ipException: IpVM) {
    this.ips.update(arrayReplaceItem(ipException, { ...ipException, selected: !ipException.selected }));
  }

  selectOrDeselectAll() {
    const allSelected = this.allSelected();
    this.ips.update((items) => items.map((ipException) => ({ ...ipException, selected: !allSelected })));
  }

  validateEdit(ipException: IpVM) {
    if (this.isNew(ipException)) this.createIpException(ipException);
    else this.updateIpException(ipException);
  }

  cancelEdit(ipException: IpVM) {
    if (this.isNew(ipException)) {
      this.deleteIpException(ipException);
    } else {
      this.ips.update(arrayReplaceItem(ipException, { ...this.tmpIpException, editing: false }));
    }
  }

  deleteIpException(ipException: IpVM) {
    if (this.isNew(ipException)) {
      this.ips.update(arrayRemoveItem(ipException));
    } else {
      this.ips.update(arrayRemoveItem(ipException));
      this.saveIps().subscribe(() => {
        this.toastr.success('WhitelistedIPDeleted');
      });
    }
  }

  ipExceptionToVm(ipException: IpWithComment): IpVM {
    return { ...ipException, editing: false, selected: false, isNew: false };
  }

  updateIpException(ipException: IpVM) {
    const error = this.validationError(ipException);
    if (error) {
      this.toastr.error(error);
      this.busy.next(false);
      return;
    }

    this.busy.next(true);
    this.ips.update(arrayReplaceItem(this.ips()[ipException.pos], ipException));
    this.saveIps().subscribe(() => {
      this.toastr.success('WhitelistedIPUpdated');
    });
  }

  startIpExceptionEditing(ipException: IpVM) {
    this.tmpIpException = { ...ipException };
    this.ips.update(arrayReplaceItem(ipException, { ...ipException, editing: true }));
    setTimeout(() => this.ipExceptionElement.nativeElement.focus(), 1);
  }

  deleteBatchException() {
    this.confirmService.confirm(
      {
        html: this.translate.instant('IPRemoveBatch'),
      },
      () => this.deleteSelected(),
    );
  }

  protected isNew(ipException: IpVM) {
    return ipException.isNew === true;
  }

  protected deleteSelected() {
    const selectedIps = this.selectedExceptions();
    if (!selectedIps.length || this.isEditing()) return;

    const selectedKeys = new Set(selectedIps.map((ip) => ip.ip));
    const remainingVm = this.ips().filter((ip) => !this.isNew(ip) && !selectedKeys.has(ip.ip));
    this.ips.set(remainingVm);
    this.saveIps().subscribe(() => {
      this.toastr.success('IPsWellDeletedFromWhitelist');
    });
  }

  protected downloadCsv() {
    const rows = this.selectedExceptions().length ? this.selectedExceptions() : this.ips();
    if (!rows.length) return;

    const csv = ['IP,Comment', ...rows.map((e) => `${e.ip},"${(e.comment ?? '').replace(/"/g, '""')}"`)].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${this.domainName}-ips.csv`; // FIXME: customize filename
    a.click();
    URL.revokeObjectURL(url);
  }

  onKeyDown(event: KeyboardEvent, ipException: IpVM): void {
    if (event.key === 'Enter' && !event.shiftKey && !this.busy.getValue()) {
      this.validateEdit(ipException);
      event.preventDefault();
    } else if (event.key === 'Escape') {
      this.cancelEdit(ipException);
    }
  }

  protected copyToClipboard() {
    const rows = this.selectedExceptions().length ? this.selectedExceptions() : this.ips();
    if (!rows.length) return;

    const text = rows.map((e) => `${e.ip}\t${e.comment ?? ''}`).join('\n');
    const html = `<table>${rows
      .map((e) => `<tr><td>${escapeHtml(e.ip)}</td><td>${escapeHtml(e.comment ?? '')}</td></tr>`)
      .join('')}</table>`;

    const done = () => this.toastr.success('Rule.ipExceptions.copySuccess');

    if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
      navigator.clipboard
        .write([
          new ClipboardItem({
            'text/plain': new Blob([text], { type: 'text/plain' }),
            'text/html': new Blob([html], { type: 'text/html' }),
          }),
        ])
        .then(done)
        .catch(() => navigator.clipboard.writeText(text).then(done));
      return;
    }

    navigator.clipboard.writeText(text).then(done);
  }

  private validationError(ipException: IpVM): string | null {
    ipException.ip = ipException.ip.trim();

    if (!isValidIpOrCidr(ipException.ip)) {
      return 'InvalidIpOrCidr';
    }

    if (hasDuplicates(this.ips().map((e) => toCidr(e.ip)))) {
      return 'AlreadyExists';
    }

    return null;
  }

  protected async pasteFromClipboard() {
    if (this.pasting) return;

    if (!navigator.clipboard?.readText) {
      this.toastr.error('ClipboardReadFailed');
      return;
    }

    try {
      const status = await navigator.permissions.query({ name: 'clipboard-read' as PermissionName });
      if (status.state === 'denied') {
        this.toastr.error('ClipboardPermissionDenied');
        return;
      }
    } catch {}

    let text: string;
    try {
      text = await navigator.clipboard.readText();
    } catch {
      this.toastr.error('ClipboardReadFailed');
      return;
    }

    this.addPastedIps(text);
  }

  private addPastedIps(text: string) {
    const parsed = parseBulkLines(text);
    if (!parsed.length) return;

    const invalid = parsed.filter((p) => !isValidIpOrCidr(p.ip)).map((p) => p.ip);
    if (invalid.length) {
      this.toastr.error(this.translate.instant('InvalidIpOrCidr') + ': ' + invalid.join(', '));
      return;
    }

    const existing = new Set(this.ips().map((e) => toCidr(e.ip)));
    const seen = new Set<string>();
    const toAdd = parsed
      .map((p) => ({ ...p, ip: toCidr(p.ip) }))
      .filter((p) => {
        if (existing.has(p.ip) || seen.has(p.ip)) return false;
        seen.add(p.ip);
        return true;
      });

    if (!toAdd.length) {
      this.toastr.error('AlreadyExists');
      return;
    }

    this.pasting = true;

    this.ips.set([
      ...this.ips(),
      ...toAdd.map((add) => this.ipExceptionToVm(add)),
    ]);
    this.saveIps().subscribe(() => {
      this.toastr.success('IPsWellAddedToWhitelist');
      this.pasting = false; // FIXME: handle errors
    });
  }
}

function escapeHtml(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

type IpVM = IpWithComment & {
  editing: boolean;
  selected?: boolean;
  isNew?: boolean;
  pos?: number;
};

export interface IpListService {
  getIps(): Observable<IpWithComment[]>;
  setIps(ipExceptions: IpWithComment[]): Observable<IpWithComment[]>;
}
